/**
 * The larger live game state at the top of game detail's Matchup tab
 * (ISSUE-1091): a diamond with the count and the outs for baseball, a field
 * strip with the ball spot for football. Nothing for a game whose state is
 * possession only; that is the header's dot. Every label is the server's.
 *
 * ISSUE-1383, v1 gamecast depth: baseball adds who is pitching and who is up,
 * each with today's line; football adds the drive so far and, on the strip,
 * where the drive started and the line to gain.
 */
import type { ReactNode } from "react";

import { BasesDiamond } from "./BasesDiamond";
import { FieldStrip } from "./FieldStrip";
import type { typeLiveStatePlayer, typeLiveStateWire } from "./liveStateTypes";
import { OutsDots } from "./OutsDots";

export type typeLiveStatePanelProps = {
  liveState: typeLiveStateWire;
  awayPlateText: string;
  homePlateText: string;
};

/** One "Pitching" or "At bat" block: the caption, the name, today's line. */
const PlayerLine = (props: {
  caption: string;
  player: typeLiveStatePlayer;
  testId: string;
}): ReactNode => (
  <div className="flex min-w-0 flex-col" data-testid={props.testId}>
    <span className="text-caption text-ink-tertiary">{props.caption}</span>
    <span className="truncate text-body-strong text-ink">
      {props.player.name}
    </span>
    {props.player.lineLabel !== null ? (
      <span className="text-small text-ink-secondary tabular-nums">
        {props.player.lineLabel}
      </span>
    ) : null}
  </div>
);

export const LiveStatePanel = (props: typeLiveStatePanelProps): ReactNode => {
  const baseball = props.liveState.baseball ?? null;
  const football = props.liveState.football ?? null;
  if (baseball === null && football === null) {
    return null;
  }
  return (
    <section
      data-testid="live-state-panel"
      className="flex flex-col gap-3 rounded-md border border-hairline bg-surface p-3"
    >
      {baseball !== null ? (
        <div className="flex items-center gap-4">
          <BasesDiamond
            isFirstOccupied={baseball.isFirstOccupied}
            isSecondOccupied={baseball.isSecondOccupied}
            isThirdOccupied={baseball.isThirdOccupied}
            size="panel"
          />
          <div className="flex flex-col gap-1">
            <span className="text-caption text-ink-tertiary">Count</span>
            <span
              data-testid="live-state-count"
              className="text-body-strong text-ink tabular-nums"
            >
              {baseball.countLabel}
            </span>
            <div className="flex items-center gap-2">
              <OutsDots outs={baseball.outs} size="panel" />
              <span
                data-testid="live-state-outs"
                className="text-body text-ink"
              >
                {baseball.outsLabel}
              </span>
            </div>
          </div>
        </div>
      ) : null}
      {baseball !== null &&
      ((baseball.pitcher ?? null) !== null ||
        (baseball.batter ?? null) !== null) ? (
        <div
          className="grid grid-cols-2 gap-3 border-t border-hairline pt-3"
          data-testid="live-state-matchup"
        >
          {baseball.pitcher != null ? (
            <PlayerLine
              caption="Pitching"
              player={baseball.pitcher}
              testId="live-state-pitcher"
            />
          ) : (
            <span />
          )}
          {baseball.batter != null ? (
            <PlayerLine
              caption="At bat"
              player={baseball.batter}
              testId="live-state-batter"
            />
          ) : null}
        </div>
      ) : null}
      {football !== null ? (
        <div className="flex flex-col gap-1">
          <span className="text-caption text-ink-tertiary">Ball on</span>
          <span
            data-testid="live-state-ball-spot"
            className="text-body-strong text-ink"
          >
            {football.ballSpotLabel}
          </span>
          {(props.liveState.downAndDistanceLabel ?? null) !== null ? (
            <span data-testid="live-state-down" className="text-body text-ink">
              {props.liveState.downAndDistanceLabel}
            </span>
          ) : null}
          <FieldStrip
            awayLabel={props.awayPlateText}
            homeLabel={props.homePlateText}
            ballSpotPercentFromAway={football.ballSpotPercentFromAway}
            driveStartPercentFromAway={football.driveStartPercentFromAway}
            firstDownPercentFromAway={football.firstDownPercentFromAway}
          />
          {(football.driveSummaryLabel ?? null) !== null ? (
            <span
              data-testid="live-state-drive"
              className="text-small text-ink-secondary tabular-nums"
            >
              <span className="text-ink-tertiary">This drive: </span>
              {football.driveSummaryLabel}
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
